import { IndustryDashboardPanel } from '@/components/dashboard/IndustryDashboardPanel';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Topbar } from '@/components/Topbar';
import { StatTile } from '@/components/StatTile';
import { Card, SectionHeader } from '@/components/Card';
import { Button } from '@/components/Button';
import { useAuth } from '@/hooks/useAuth';
import { dashboardService, DashboardOverview, PerRepRow } from '@/services/dashboard.service';
import { botFlowsService } from '@/services/bot-flows.service';
import { businessProfileService } from '@/services/business-profile.service';
import { leadsService, Lead } from '@/services/leads.service';
import { documentsService, CashflowSnapshot } from '@/services/documents.service';
import { useActiveAccount } from '@/hooks/useActiveAccount';
import { useModuleAccess } from '@/contexts/ModuleAccessContext';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { industryIcon, industryOption } from '@/lib/industries';
import {
  Users, Folder, Upload, MessageCircle, Activity, ArrowUpRight,
  Sparkles, Send, FileText, Megaphone, Camera, Smartphone, ShieldCheck, Bot, Clock,
  BarChart3, DollarSign, AlertCircle, Trophy, UserRound,
} from 'lucide-react';
import clsx from 'clsx';

function HealthChip({ rating }: { rating?: string }) {
  const map: Record<string, { label: string; cls: string; dot: string }> = {
    GREEN:  { label: 'Healthy',  cls: 'bg-success-soft text-success border-success/20',  dot: 'bg-success' },
    YELLOW: { label: 'Warning',  cls: 'bg-warning-soft text-warning border-warning/30',  dot: 'bg-warning' },
    RED:    { label: 'At risk',  cls: 'bg-danger-soft text-danger border-danger/20',     dot: 'bg-danger'  },
  };
  const m = map[rating ?? ''] ?? { label: rating ?? '—', cls: 'bg-surface-alt text-text-muted border-border', dot: 'bg-text-muted' };
  return (
    <span className={clsx('inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold', m.cls)}>
      <span className={clsx('h-1.5 w-1.5 rounded-full', m.dot)} />
      {m.label}
    </span>
  );
}

function relTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24); if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

const STUCK_HOURS = 24;

export default function Dashboard() {
  const { profile } = useAuth();
  const { info: workspace } = useWorkspace();
  const { active: activeAccount, loading: accountsLoading } = useActiveAccount();
  const { has: hasModule, loading: modulesLoading } = useModuleAccess();
  // Invoices live in Documents; asking without the module only earns a "not enabled" toast.
  const showCashflow = !modulesLoading && hasModule('documents');
  const [data, setData] = useState<DashboardOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [showBotNudge, setShowBotNudge] = useState(false);
  const [stuckLeads, setStuckLeads] = useState<Lead[] | null>(null);
  const [cashflow, setCashflow] = useState<CashflowSnapshot | null>(null);
  const [periodDays, setPeriodDays] = useState<number>(30);
  const [perRep, setPerRep] = useState<PerRepRow[] | null>(null);

  useEffect(() => {
    if (!showCashflow) { setCashflow(null); return; }
    documentsService.cashflow().then(setCashflow).catch(() => undefined);
  }, [showCashflow]);

  useEffect(() => {
    setLoading(true);
    dashboardService.overview(periodDays)
      .then(setData)
      .catch(() => undefined)
      .finally(() => setLoading(false));
    dashboardService.perRep(periodDays)
      .then(setPerRep)
      .catch(() => setPerRep([]));
  }, [periodDays]);

  useEffect(() => {
    // Gate on the account picker: leadsService.list treats a missing accountUid
    // as "all accounts" (leads.service.ts:93). This effect used to run once on
    // mount with accountUid undefined and never re-run, so the widget showed
    // leads from every connected WhatsApp number for the whole session.
    if (accountsLoading) return;

    // Stuck leads widget — new leads with no activity in the past 24h.
    // Pulls a recent slice and filters client-side; cheap enough at this scale.
    leadsService.list({
      status: 'new',
      accountUid: activeAccount?.accountUid,
      limit: 100,
    } as Parameters<typeof leadsService.list>[0])
      .then((r) => {
        const cutoff = Date.now() - STUCK_HOURS * 60 * 60 * 1000;
        const stuck = r.items.filter((l) => new Date(l.updatedAt ?? l.createdAt).getTime() < cutoff);
        setStuckLeads(stuck.slice(0, 8));
      })
      .catch(() => setStuckLeads([]));

    // Check if we should show the bot setup nudge
    Promise.all([
      botFlowsService.list().catch(() => [] as ReturnType<typeof botFlowsService.list> extends Promise<infer T> ? T : never[]),
      businessProfileService.getByCompany().catch(() => null),
    ]).then(([flows, bProfile]) => {
      const hasActiveFlow = (flows as { isActive: boolean }[]).some(f => f.isActive);
      const hasProfile = bProfile !== null;
      setShowBotNudge(!hasActiveFlow && !hasProfile);
    }).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAccount?.accountUid, accountsLoading]);

  const greet = (() => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 18) return 'Good afternoon';
    return 'Good evening';
  })();
  const firstName = profile?.firstName?.trim() || 'there';
  const businessName = workspace?.companyName?.trim() || '';
  const BusinessIcon = industryIcon(workspace?.industry);
  const industryLabel = industryOption(workspace?.industry)?.label;

  return (
    <>
      <Topbar
        title="Dashboard"
        breadcrumb="Overview"
        showSearch
        right={
          <Link to="/campaigns/new">
            <Button size="md" iconLeft={<Send size={14} />}>New campaign</Button>
          </Link>
        }
      />

      <div className="px-7 py-7 w-full">
        {profile?.companyUid && <IndustryDashboardPanel key={profile.companyUid} />}
        {/* Bot setup nudge — shown only when 0 active flows AND no business profile */}
        {showBotNudge && (
          <div className="mb-6 flex items-center gap-4 rounded-2xl border border-primary/30 bg-primary-soft/40 px-5 py-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-soft text-primary">
              <Bot size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-text-primary">Set up your bot in 2 minutes</div>
              <div className="text-xs text-text-muted">Describe your business and we'll generate a WhatsApp qualification flow automatically.</div>
            </div>
            <Link to="/settings/business-profile">
              <Button size="sm" iconLeft={<Sparkles size={13} />}>Get started</Button>
            </Link>
            <button
              onClick={() => setShowBotNudge(false)}
              className="ml-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-text-muted hover:bg-surface-alt transition-colors text-lg leading-none"
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        )}

        {/* Hero greeting */}
        <div className="relative mb-8 overflow-hidden rounded-3xl border border-border bg-gold-gradient-soft px-6 py-7 shadow-soft sm:px-8">
          <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-gold-gradient opacity-20 blur-3xl" />
          <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              {businessName ? (
                <Link
                  to="/settings/business-profile"
                  title="Business profile"
                  className="inline-flex max-w-full items-center gap-2 rounded-full border border-primary/25 bg-surface/70 py-1 pl-1 pr-3 backdrop-blur-sm transition-colors hover:border-primary/50"
                >
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-soft text-primary">
                    <BusinessIcon size={13} />
                  </span>
                  <span className="truncate text-sm font-semibold text-text-primary">{businessName}</span>
                  {industryLabel && <span className="hidden shrink-0 text-xs text-text-muted sm:inline">· {industryLabel}</span>}
                </Link>
              ) : (
                <div className="inline-flex items-center gap-2 rounded-full border border-primary/25 bg-surface/70 px-3 py-1 backdrop-blur-sm">
                  <Sparkles size={12} className="text-primary" />
                  <span className="text-2xs font-semibold uppercase tracking-[0.14em] text-primary">Today</span>
                </div>
              )}
              <h2 className="mt-3 font-display text-[26px] font-bold leading-tight tracking-tight text-text-primary sm:text-[30px]">
                {greet}, {firstName}.
              </h2>
              <p className="mt-1.5 max-w-xl text-[14px] text-text-secondary">
                Here's a quick view of {businessName ? `${businessName}'s` : 'your'} audience, campaigns, and account health.
              </p>
            </div>
            {data?.health && (
              <div className="rounded-2xl border border-border bg-surface/80 p-4 backdrop-blur-sm">
                <div className="text-2xs font-semibold uppercase tracking-[0.14em] text-text-muted">WhatsApp</div>
                <div className="mt-2 flex items-center gap-3">
                  <HealthChip rating={data.health.qualityRating} />
                  <div className="text-xs text-text-muted">
                    Tier <span className="font-semibold text-text-primary">{data.health.messagingLimitTier ?? '—'}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Period selector */}
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-text-muted mr-1">Period</span>
          {[
            { d: 1, label: 'Today' },
            { d: 7, label: '7d' },
            { d: 30, label: '30d' },
            { d: 90, label: '90d' },
          ].map((p) => (
            <button
              key={p.d}
              onClick={() => setPeriodDays(p.d)}
              className={clsx(
                'rounded-full border px-3 py-1 text-xs font-semibold transition',
                periodDays === p.d
                  ? 'border-primary bg-primary-soft text-primary'
                  : 'border-border bg-surface text-text-secondary hover:bg-surface-alt',
              )}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* KPI grid */}
        <div className={clsx('mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2', showCashflow ? 'lg:grid-cols-4' : 'lg:grid-cols-3')}>
          {loading ? null : data && (
            <>
              <StatTile
                label="New contacts"
                value={(data.newContactsInPeriod ?? 0).toLocaleString()}
                delta={`Last ${periodDays}d`}
                trend="neutral"
                icon={<Users size={17} />}
                accent="green"
              />
              <StatTile
                label="New leads"
                value={(data.newLeadsInPeriod ?? 0).toLocaleString()}
                delta={`Last ${periodDays}d`}
                trend="neutral"
                icon={<Sparkles size={17} />}
                accent="amber"
              />
              <StatTile
                label="Deals won"
                value={(data.dealsWonInPeriod ?? 0).toLocaleString()}
                delta={`Last ${periodDays}d`}
                trend="neutral"
                icon={<BarChart3 size={17} />}
                accent="gold"
              />
              {showCashflow && (
                <StatTile
                  label="Cashflow"
                  value={cashflow?.paid?.[0] ? `${cashflow.paid[0].total.toFixed(0)} ${cashflow.paid[0]._id}` : '—'}
                  delta={`${cashflow?.outstanding?.length ?? 0} outstanding`}
                  trend="neutral"
                  icon={<DollarSign size={17} />}
                  accent="blue"
                />
              )}
            </>
          )}
        </div>

        {/* KPI grid (totals — unaffected by period) */}
        <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => <div key={i} className="skeleton h-32 rounded-2xl" />)
          ) : data ? (
            <>
              <StatTile
                label="Contacts"
                value={data.contactCount.toLocaleString()}
                delta={`${data.optedInCount.toLocaleString()} opted in`}
                trend="neutral"
                icon={<Users size={17} />}
                accent="gold"
              />
              <StatTile
                label="Groups"
                value={data.groupCount.toLocaleString()}
                delta="Active segments"
                trend="neutral"
                icon={<Folder size={17} />}
                accent="blue"
              />
              <StatTile
                label="Recent imports"
                value={data.recentImports.length}
                delta="Last 5 imports"
                trend="neutral"
                icon={<Upload size={17} />}
                accent="amber"
              />
              <StatTile
                label="Account"
                value={data.health?.qualityRating ?? '—'}
                delta={data.health?.messagingLimitTier ?? '—'}
                trend="neutral"
                icon={<MessageCircle size={17} />}
                accent={data.health?.qualityRating === 'GREEN' ? 'green' : data.health?.qualityRating === 'RED' ? 'red' : 'gold'}
              />
            </>
          ) : null}
        </div>

        {data?.sales && <Card className="mb-4"><SectionHeader title="Sales outcomes" subtitle={`Closed in the last ${periodDays} days · open pipeline is current · values stay in their own currency`} /><div className="mb-4 flex flex-wrap gap-5 text-sm text-text-secondary"><span><strong>{data.sales.dealsWon}</strong> won</span><span><strong>{data.sales.dealsLost}</strong> lost</span><span><strong>{data.sales.openDeals}</strong> open</span></div><div className="overflow-x-auto"><table className="w-full text-left text-sm"><thead className="border-b border-border text-xs text-text-muted"><tr><th className="py-2">Currency</th><th>Won value</th><th>Average won deal</th><th>Open pipeline</th></tr></thead><tbody>{data.sales.byCurrency.map(row => <tr key={row.currency} className="border-b border-border last:border-0"><td className="py-3">{row.currency === 'UNKNOWN' ? 'Unavailable' : row.currency}</td><td>{row.revenueWon.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td><td>{row.averageWonDeal.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td><td>{row.pipelineValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td></tr>)}</tbody></table></div>{!data.sales.byCurrency.length && <p className="py-4 text-sm text-text-muted">No sales values recorded yet.</p>}{!!data.sales.unresolvedOutcomes && <p className="mt-3 text-xs text-text-muted">{data.sales.unresolvedOutcomes} older deals need stage/closure review and are excluded from outcome totals.</p>}</Card>}
        {/* Cashflow snapshot — invoice receivables, grouped by currency */}
        {cashflow && (cashflow.paid.length + cashflow.outstanding.length + cashflow.overdue.length) > 0 && (
          <Card className="mb-8">
            <div className="flex items-center justify-between mb-3">
              <SectionHeader title="Cashflow" subtitle="Invoice receivables (per currency)" />
              <Link to="/documents" className="text-xs font-semibold text-primary hover:underline inline-flex items-center gap-1">
                View invoices <ArrowUpRight size={12} />
              </Link>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <DashCashTile
                label="Paid"
                rows={cashflow.paid}
                tone="success"
                icon={<DollarSign size={16} />}
              />
              <DashCashTile
                label="Outstanding"
                rows={cashflow.outstanding}
                tone="warning"
                icon={<Clock size={16} />}
              />
              <DashCashTile
                label="Overdue"
                rows={cashflow.overdue}
                tone="danger"
                icon={<AlertCircle size={16} />}
              />
            </div>
          </Card>
        )}

        {/* Per-rep performance — only render when there's actual activity to show */}
        {perRep && perRep.length > 0 && (
          <Card className="mb-8" padded={false}>
            <div className="flex items-center justify-between border-b border-border px-5 py-3">
              <div>
                <div className="font-display text-sm font-semibold text-text-primary">Team performance</div>
                <div className="mt-0.5 text-xs text-text-muted">
                  Last {periodDays} days · sorted by deals won
                </div>
              </div>
              <Link
                to="/reports"
                className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
              >
                Full reports <ArrowUpRight size={12} />
              </Link>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-surface-alt/40 text-2xs uppercase tracking-wider text-text-muted">
                  <tr>
                    <th className="px-5 py-2 text-left font-semibold">Rep</th>
                    <th className="px-3 py-2 text-right font-semibold">Leads</th>
                    <th className="px-3 py-2 text-right font-semibold">Owned deals</th>
                    <th className="px-3 py-2 text-right font-semibold">Won</th>
                    <th className="px-3 py-2 text-right font-semibold">Lost</th>
                    <th className="px-3 py-2 text-right font-semibold">Won value</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {perRep.map((r, idx) => (
                    <tr key={r.userUid} className="hover:bg-surface-alt">
                      <td className="px-5 py-2.5">
                        <div className="flex items-center gap-2">
                          {idx === 0 ? (
                            <Trophy size={14} className="shrink-0 text-warning" />
                          ) : (
                            <UserRound size={14} className="shrink-0 text-text-muted" />
                          )}
                          <div className="min-w-0">
                            <div className="truncate font-medium text-text-primary">{r.fullName}</div>
                            <div className="truncate text-2xs text-text-muted">{r.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-text-secondary">
                        {r.leadsAssigned}
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-text-secondary">
                        {r.dealsOwned}
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-success">
                        {r.dealsWon}
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-text-muted">
                        {r.dealsLost || '—'}
                      </td>
                      <td className="px-3 py-2.5 text-right text-xs">
                        {Object.keys(r.wonValueByCurrency).length === 0 ? (
                          <span className="text-text-muted">—</span>
                        ) : (
                          <div className="space-y-0.5">
                            {Object.entries(r.wonValueByCurrency).map(([cur, v]) => (
                              <div key={cur} className="tabular-nums">
                                <span className="text-text-muted">{cur} </span>
                                <span className="font-semibold text-text-primary">
                                  {v.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Quick actions */}
        <div className="mb-8">
          <SectionHeader title="Jump in" subtitle="Frequently used" />
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
            <QuickAction to="/contacts/new" icon={<Users size={16} />} label="Add contact" />
            <QuickAction to="/templates/new" icon={<FileText size={16} />} label="New template" />
            <QuickAction to="/campaigns/new" icon={<Send size={16} />} label="New campaign" />
            <QuickAction to="/facebook/campaigns/new" icon={<Megaphone size={16} />} label="Meta campaign" />
            <QuickAction to="/instagram/workspace" icon={<Camera size={16} />} label="IG-only campaign" />
            <QuickAction to="/reports" icon={<BarChart3 size={16} />} label="Business report" />
            <QuickAction to="/leads/ai-analytics" icon={<Sparkles size={16} />} label="AI analytics" />
            <QuickAction to="/whatsapp-account" icon={<Smartphone size={16} />} label="WA Account" />
          </div>
        </div>

        {/* Stuck leads — when more than 0, surface above the imports/activity section */}
        {stuckLeads && stuckLeads.length > 0 && (
          <Card padded={false} className="mb-5 overflow-hidden border-warning/40">
            <div className="border-b border-border bg-warning-soft/40 px-5 py-3">
              <SectionHeader
                title={`${stuckLeads.length} lead${stuckLeads.length === 1 ? '' : 's'} need attention`}
                subtitle={`No reply for ${STUCK_HOURS}h+. These are new leads still waiting for a human.`}
                icon={<Clock size={15} className="text-warning" />}
                action={
                  <Link to="/leads?needsAttention=1" className="text-xs font-medium text-warning hover:underline">
                    Open all →
                  </Link>
                }
                className="mb-0"
              />
            </div>
            <div className="divide-y divide-border">
              {stuckLeads.map((l) => {
                const initials = (l.profileName?.trim() || l.phone).slice(0, 2).toUpperCase();
                const ageHours = Math.floor((Date.now() - new Date(l.updatedAt ?? l.createdAt).getTime()) / 3_600_000);
                return (
                  <Link
                    key={l.leadUid}
                    to={`/chats?lead=${l.leadUid}`}
                    className="flex items-center gap-3 px-5 py-3 hover:bg-surface-alt/60 transition-colors"
                  >
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-warning-soft text-[11px] font-bold text-warning">
                      {initials}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[13.5px] font-semibold text-text-primary">{l.profileName?.trim() || l.phone}</div>
                      <div className="text-2xs text-text-muted">{l.phone} · stuck {ageHours}h</div>
                    </div>
                    <ArrowUpRight size={13} className="text-text-muted shrink-0" />
                  </Link>
                );
              })}
            </div>
          </Card>
        )}

        {/* Two-column lower section */}
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
          {/* Recent imports */}
          <Card padded={false} className="overflow-hidden xl:col-span-2">
            <div className="border-b border-border px-5 py-4">
              <SectionHeader
                title="Recent imports"
                subtitle="Latest CSV uploads and their results"
                icon={<Upload size={15} />}
                action={
                  <Link to="/contacts/import" className="text-xs font-medium text-primary hover:underline">
                    Upload CSV →
                  </Link>
                }
                className="mb-0"
              />
            </div>
            <div className="divide-y divide-border">
              {loading ? (
                Array.from({ length: 3 }).map((_, i) => <div key={i} className="skeleton mx-5 my-3 h-12 rounded-lg" />)
              ) : !data?.recentImports.length ? (
                <EmptyRow icon={<Upload size={18} />} text="No imports yet" />
              ) : (
                data.recentImports.map((r) => (
                  <div key={r.importUid} className="flex items-center justify-between gap-3 px-5 py-3.5 hover:bg-surface-alt/60 transition-colors">
                    <div className="flex min-w-0 items-center gap-3">
                      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-warning-soft text-warning">
                        <Upload size={14} />
                      </div>
                      <div className="min-w-0">
                        <div className="truncate text-[13.5px] font-semibold text-text-primary">{r.filename}</div>
                        <div className="text-2xs text-text-muted">
                          {r.inserted.toLocaleString()} / {r.totalRows.toLocaleString()} inserted · {r.status}
                        </div>
                      </div>
                    </div>
                    <div className="text-2xs text-text-muted shrink-0">{relTime(r.createdAt)}</div>
                  </div>
                ))
              )}
            </div>
          </Card>

          {/* Activity feed */}
          <Card padded={false} className="overflow-hidden">
            <div className="border-b border-border px-5 py-4">
              <SectionHeader
                title="Activity"
                subtitle="Recent actions in your workspace"
                icon={<Activity size={15} />}
                className="mb-0"
              />
            </div>
            <div className="divide-y divide-border">
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => <div key={i} className="skeleton mx-5 my-3 h-10 rounded-lg" />)
              ) : !data?.recentActivity.length ? (
                <EmptyRow icon={<Activity size={18} />} text="Nothing yet" />
              ) : (
                data.recentActivity.slice(0, 8).map((a) => (
                  <div key={a._id} className="flex items-start gap-3 px-5 py-3 hover:bg-surface-alt/60 transition-colors">
                    <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary-soft text-primary">
                      <ShieldCheck size={12} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-mono text-[12.5px] font-medium text-text-primary truncate">{a.action}</div>
                      <div className="text-2xs text-text-muted">{relTime(a.createdAt)}</div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}

const QuickAction: React.FC<{ to: string; icon: React.ReactNode; label: string }> = ({ to, icon, label }) => (
  <Link
    to={to}
    className="group relative flex items-center gap-3 overflow-hidden rounded-xl border border-border bg-surface px-4 py-3 shadow-soft transition-all hover:-translate-y-0.5 hover:border-primary hover:shadow-elevated"
  >
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary transition-colors group-hover:bg-gold-gradient group-hover:text-on-primary">
      {icon}
    </span>
    <span className="text-[13px] font-semibold text-text-primary">{label}</span>
    <ArrowUpRight size={13} className="ml-auto text-text-muted transition-all group-hover:text-primary group-hover:translate-x-0.5" />
  </Link>
);

const EmptyRow: React.FC<{ icon: React.ReactNode; text: string }> = ({ icon, text }) => (
  <div className="flex flex-col items-center gap-2 px-5 py-10 text-text-muted">
    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-surface-alt">{icon}</div>
    <div className="text-sm">{text}</div>
  </div>
);

const TONE_CLS: Record<'success' | 'warning' | 'danger', { bar: string; iconBg: string; label: string }> = {
  success: { bar: 'border-success/40 bg-success-soft/30', iconBg: 'bg-success-soft text-success', label: 'text-success' },
  warning: { bar: 'border-warning/40 bg-warning-soft/30', iconBg: 'bg-warning-soft text-warning', label: 'text-warning' },
  danger:  { bar: 'border-danger/40 bg-danger-soft/30',   iconBg: 'bg-danger-soft text-danger',   label: 'text-danger'  },
};

function fmtMoney(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency.toUpperCase(), maximumFractionDigits: 2 }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency.toUpperCase()}`;
  }
}

const DashCashTile: React.FC<{
  label: string;
  rows: Array<{ _id: string; total: number; count: number }>;
  tone: 'success' | 'warning' | 'danger';
  icon: React.ReactNode;
}> = ({ label, rows, tone, icon }) => {
  const cls = TONE_CLS[tone];
  const totalCount = rows.reduce((sum, r) => sum + r.count, 0);
  return (
    <div className={clsx('rounded-xl border p-4', cls.bar)}>
      <div className="flex items-center gap-2 mb-3">
        <span className={clsx('flex h-8 w-8 items-center justify-center rounded-lg', cls.iconBg)}>{icon}</span>
        <div>
          <div className={clsx('text-sm font-bold', cls.label)}>{label}</div>
          <div className="text-2xs text-text-muted">{totalCount} invoice{totalCount === 1 ? '' : 's'}</div>
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="text-xs text-text-muted">—</div>
      ) : (
        <div className="space-y-1">
          {rows.map((r) => (
            <div key={r._id} className="flex items-baseline justify-between text-sm">
              <span className="font-mono text-2xs uppercase text-text-muted">{r._id}</span>
              <span className="font-semibold text-text-primary tabular-nums">{fmtMoney(r.total, r._id)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
